import React, { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { FaTimes, FaPlus } from "react-icons/fa";
import { API_URL } from "../constants";
import { UserTypes } from "../constants";

const MachineAgencyChiefs = ({ agence }) => {
  const [chefAgence, setChefAgence] = useState(null);
  const [chefAtelier, setChefAtelier] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [usersWithoutAgence, setUsersWithoutAgence] = useState([]);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState("");

  const user = JSON.parse(localStorage.getItem("user"));
  const userType = user.type;
  const token = user.auth_token;
  const headers = useMemo(() => ({
    Accept: "application/json", 
    "Content-Type": "application/json",
    Authorization: `Bearer ${token}`
  }), [token]);
  const getAgenceChiefs = useCallback(async (agenceId) => {
    // Le back renvoie 404 quand il n'y a aucun chef : chaque requête est traitée séparément
    const [chefAgRes, chefAtRes] = await Promise.allSettled([
      axios.get(`${API_URL}/agences/${agenceId}/chefag`, { headers }),
      axios.get(`${API_URL}/agences/${agenceId}/chefat`, { headers }),
    ]);
    [chefAgRes, chefAtRes].forEach((res) => {
      if (res.status === "rejected" && res.reason.response?.status !== 404) {
        console.error("Erreur récupération chefs :", res.reason.message);
      }
    });
    return {
      chefAgence: chefAgRes.status === "fulfilled" ? chefAgRes.value.data.data : null,
      chefAtelier: chefAtRes.status === "fulfilled" ? chefAtRes.value.data.data : null,
    };
  }, [headers]);

  const fetchUsersWithoutAgence = useCallback(async () => {
  try {
    const response = await axios.get(`${API_URL}/users?limit=1000&offset=0`, { headers });

    setUsersWithoutAgence(
      response.data.data.filter((u) => u.agence == null && (u.type === 2 || u.type === 3))
    );
  } catch (error) {
    console.error("Erreur utilisateurs sans agence :", error.message);
  }
}, [headers]);

  useEffect(() => {
    if (agence) {
      getAgenceChiefs(agence.id).then(({ chefAgence, chefAtelier }) => {
        setChefAgence(chefAgence);
        setChefAtelier(chefAtelier);
      });
    }
  }, [agence, getAgenceChiefs]);

  useEffect(() => {
    if (editMode) fetchUsersWithoutAgence();
  }, [editMode, fetchUsersWithoutAgence]);

  const handleConfirmRemove = async (userId) => {
    try {
      await axios.delete(`${API_URL}/agences/${agence.id}/removeAgent/${userId}`, { headers });
      window.location.reload();
    } catch (error) {
      console.error("Erreur suppression chef :", error.message);
    }
  };

  const handleAddUserToAgence = async () => {
    try {
      await axios.post(`${API_URL}/agences/${agence.id}/users`, { id: selectedUserToAdd }, { headers });
      const { chefAgence: ca, chefAtelier: cat } = await getAgenceChiefs(agence.id);
      setChefAgence(ca);
      setChefAtelier(cat);
      setSelectedUserToAdd("");
      setEditMode(false);
    } catch (error) {
      console.error("Erreur ajout utilisateur :", error.message);
    }
  };

  const ChefList = ({ title, chefs }) => (
    <div className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">{title}</span>
      {chefs && chefs.length > 0 ? (
        chefs.map((chef) => (
          <div key={chef.id} className="flex items-center gap-2 text-sm text-gray-700">
            <span>{chef.nom} {chef.prenom}</span>
            {userType === UserTypes.SUPER && (
              <FaTimes
                className="cursor-pointer text-gray-300 hover:text-red-500 transition"
                onClick={() => handleConfirmRemove(chef.id)}
              />
            )}
          </div>
        ))
      ) : (
        <span className="text-sm italic text-gray-400">Aucun</span>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <ChefList title="Chef d'agence" chefs={chefAgence} />
      <ChefList title="Chefs d'atelier" chefs={chefAtelier} />

      {(userType === UserTypes.SUPER || userType === UserTypes.ADMIN) && !editMode && (
        <button
          onClick={() => setEditMode(true)}
          className="flex items-center gap-1.5 text-sm text-loginBordure hover:text-blue-900 font-medium transition w-fit mt-1"
        >
          <FaPlus className="text-xs" />
          Ajouter un utilisateur
        </button>
      )}

      {editMode && (userType === UserTypes.SUPER || userType === UserTypes.ADMIN) && (
        <div className="flex flex-col gap-2 mt-1 pt-3 border-t border-gray-100">
          <select
            value={selectedUserToAdd}
            onChange={(e) => setSelectedUserToAdd(e.target.value)}
            className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-loginBordure/30 focus:border-loginBordure transition"
          >
            <option value="">Sélectionner un utilisateur</option>
            {usersWithoutAgence.map((u) => (
              <option key={u.id} value={u.id}>
                {u.nom} {u.prenom} ({u.type === 2 ? "Chef d'agence" : "Chef d'atelier"})
              </option>
            ))}
          </select>
          <div className="flex gap-2">
            <button onClick={handleAddUserToAgence} className="flex-1 px-4 py-2 bg-loginBordure hover:bg-blue-900 text-white text-sm font-semibold rounded-xl transition">
              Ajouter
            </button>
            <button onClick={() => setEditMode(false)} className="flex-1 px-4 py-2 bg-red-500 hover:bg-red-700 text-white text-sm font-semibold rounded-xl transition">
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MachineAgencyChiefs;